import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
// por su ruta: el index de ai-chat trae el AI SDK y esto va en el bundle inicial
import { BpAgentLauncherComponent } from '@bp-core/src/lib/ai-chat/components/launcher/bp-agent-launcher.component';
import { SudoAiChatLoader } from '../sudo-ai-chat.loader';

/**
 * Botón flotante del Asesor IA del sudo y lugar donde se monta el panel lateral (049, T095). Va en el bundle inicial y
 * es liviano: el chat se descarga al pasar el mouse o al abrirlo, y el panel se monta con el inyector del chat (el
 * mismo de la página `/ai-chat`: una sola conversación en curso). El botón es el de bp-core, con el aspecto del portal.
 */
@Component({
  selector: 'sudo-ai-chat-launcher',
  standalone: true,
  imports: [BpAgentLauncherComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!hidden() && !panelOpen()) {
      <bp-agent-launcher
        label="Consultar al Asesor IA"
        closeLabel="Cerrar el Asesor IA"
        previewTitle="El Asesor IA respondió"
        [busy]="busy()"
        [unread]="unread()"
        [preview]="preview()"
        [animateEnter]="!mounted()"
        (activate)="toggle()"
        (openPreview)="toggle()"
        (intent)="loader.preload()"
      />
    }
    @if (loadState() === 'error') {
      <p class="sudo-fab-error" role="alert">
        No se pudo cargar el Asesor IA.
        <button type="button" (click)="toggle()">Reintentar</button>
      </p>
    }
    <ng-container #panel />
  `,
  styleUrl: './sudo-ai-chat-launcher.component.scss',
})
export class SudoAiChatLauncherComponent {
  readonly loader = inject(SudoAiChatLoader);
  /** En la página `/ai-chat` no se muestran ni el botón ni el panel. */
  readonly hidden = input(false);

  private readonly panelHost = viewChild.required('panel', { read: ViewContainerRef });
  private panelRef: ComponentRef<unknown> | null = null;
  readonly mounted = signal(false);

  readonly loadState = this.loader.loadState;
  readonly panelOpen = computed(() => this.mounted() && !!this.loader.loaded?.session.open());
  /** Respondiendo, o descargando el chat. */
  readonly busy = computed(
    () =>
      this.loadState() === 'loading' || (this.mounted() && !!this.loader.loaded?.session.busy()),
  );
  readonly unread = computed(() => this.mounted() && !!this.loader.loaded?.session.unread());
  readonly preview = computed(() =>
    this.unread() ? this.loader.loaded?.session.preview() || null : null,
  );

  constructor() {
    // Quien abre el chat por fuera del botón ("Analizar con IA" en una transacción o una solicitud) solo abre la
    // sesión: el panel se monta aquí, una sola vez, para que se vea (T147). En `/ai-chat` la página ya es el chat.
    effect(() => {
      if (this.loadState() !== 'ready' || this.hidden()) return;
      const loaded = this.loader.loaded;
      if (loaded?.session.open()) untracked(() => this.ensurePanel(loaded));
    });
  }

  async toggle(): Promise<void> {
    try {
      const loaded = await this.loader.load();
      this.ensurePanel(loaded);
      loaded.session.togglePanel();
    } catch {
      /* el estado de carga muestra el error y "Reintentar" */
    }
  }

  /** Monta el panel lateral la primera vez; después no hace nada (nunca hay dos). */
  private ensurePanel(loaded: NonNullable<SudoAiChatLoader['loaded']>): void {
    if (this.panelRef) return;
    this.panelRef = this.panelHost().createComponent(loaded.panel, {
      environmentInjector: loaded.injector,
    });
    this.mounted.set(true);
  }
}
